import { EventStreamContentType, fetchEventSource } from '@microsoft/fetch-event-source';

import { isRefusedCredential, refreshAccessToken } from '@/model/client';
import {
  type EtlLiveEvent,
  parseRetryAfterMs,
  toEtlLiveEvent,
} from '@/model/etlSource/etlSource.model';
import { API_URL } from '@/shared/config/env';
import { logger } from '@/shared/helpers/logger';

export type SseConnectionState = 'connecting' | 'open' | 'closed';

export interface SseCallbacks {
  onEvent: (event: EtlLiveEvent) => void;
  onResync: () => void;
  onStateChange?: (state: SseConnectionState) => void;
}

export class FatalStreamError extends Error {}

export class TokenRefreshedError extends Error {}

export class StreamRefusedError extends Error {
  readonly retryAfterMs: number;

  constructor(message: string, retryAfterMs: number) {
    super(message);
    this.retryAfterMs = retryAfterMs;
  }
}

function buildHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = { Accept: EventStreamContentType };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return headers;
}

async function readErrorBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function assertStreamOpened(response: Response, token: string | null): Promise<void> {
  const contentType = response.headers.get('content-type') ?? '';
  if (response.ok && contentType.startsWith(EventStreamContentType)) return;

  if (response.status === 401) {
    const body = await readErrorBody(response);

    if (isRefusedCredential(response.status, body)) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) throw new FatalStreamError('session expired');

      throw new TokenRefreshedError('token refreshed');
    }

    if (!token) throw new Error(`stream unauthenticated: ${response.status}`);

    throw new FatalStreamError(`stream refused: ${response.status}`);
  }

  if (response.status === 503) {
    throw new StreamRefusedError(
      `stream refused: ${response.status}`,
      parseRetryAfterMs(response.headers.get('retry-after')),
    );
  }

  if (response.status >= 400 && response.status < 500 && response.status !== 429) {
    throw new FatalStreamError(`stream refused: ${response.status}`);
  }

  throw new Error(`stream unavailable: ${response.status}`);
}

export function openStreamSession(
  projectId: string,
  token: string | null,
  callbacks: SseCallbacks,
  signal: AbortSignal,
  onConnected: () => void,
): Promise<void> {
  callbacks.onStateChange?.('connecting');

  return fetchEventSource(`${API_URL}/api/projects/${projectId}/etl-sources/events`, {
    signal,
    headers: buildHeaders(token),
    openWhenHidden: false,

    async onopen(response): Promise<void> {
      await assertStreamOpened(response, token);
      onConnected();
      callbacks.onStateChange?.('open');
      callbacks.onResync();
    },

    onmessage(message): void {
      if (!message.data) return;

      try {
        const event = toEtlLiveEvent(message.data);
        if (event) callbacks.onEvent(event);
      } catch (error) {
        logger.warn('discarding unparseable live-update frame', {
          error: error instanceof Error ? error : new Error(String(error)),
          log_source: 'liveUpdates',
        });
      }
    },

    onclose(): void {
      throw new Error('stream closed by server');
    },

    onerror(error): never {
      throw error;
    },
  });
}
